// Imports
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { categoryMapping } from '..';
import Button from './button';


// Styled components
const Wrapper = styled.div`
	display: flex;
	width: fit-content;
	padding: 0.5rem;
	align-items: center;
	overflow: hidden;
	
	flex-wrap: wrap;
	justify-content: center;
	
	border: 0.125rem solid hsl(206, 25%, 20%);
	
	margin-top: 3.75rem;
	
	position: relative;
	
	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		box-shadow: inset 0 0 0 0.5rem hsl(0, 0%, 100%);
		
		border-radius: 624.9375rem;
	}
	
	border-radius: 624.9375rem;
`;

const ActiveBubble = styled.div<{ $isPositioned: boolean }>`
	background: hsl(96, 54%, 59%);
	height: 2.5rem;
	position: absolute;
	border-radius: 624.9375rem;
	
	${({ $isPositioned }) =>
		$isPositioned &&
		css`
			transition: all 0.3s ease 0s;
		`}
`;


// Define the accepted props
interface Props {
	view: keyof typeof categoryMapping;
	setView: React.Dispatch<React.SetStateAction<keyof typeof categoryMapping>>;
}


// Function component
const Toggle: React.FC<Props> = ({ view, setView }) => {
	// Use state
	const [bubbleStyle, setBubbleStyle] = useState<React.CSSProperties>({});
	const [isHydrated, setIsHydrated] = useState(false);
	const [isPositioned, setIsPositioned] = useState(false);
	
	
	// Use ref
	const wrapperRef = useRef<HTMLDivElement>(null);
	const viewRef = useRef(view);
	viewRef.current = view;
	
	
	// Function that updates the bubble style
	const updateBubble = useCallback(() => {
		// Return early
		if (!wrapperRef.current) {
			return;
		}
		
		
		// Find the active button
		const activeButton = wrapperRef.current.querySelector(`[data-identifier="${viewRef.current}"]`);
		
		if (!(activeButton instanceof HTMLElement)) {
			return;
		}
		
		
		// Update the bubble to match the active button's position and size
		setBubbleStyle({
			left: activeButton.offsetLeft,
			top: activeButton.offsetTop,
			width: activeButton.offsetWidth,
			height: activeButton.offsetHeight,
		});
	}, []);
	
	
	// Reveal the active bubble after the initial paint, once the buttons can be measured
	//  -> Skipped while prerendering, so the snapshot captures the static buttons without a mispositioned bubble
	useEffect(() => {
		if (window.IS_PRERENDERING) {
			return;
		}
		
		setIsHydrated(true);
		const frame = requestAnimationFrame(() => setIsPositioned(true));
		
		return () => {
			cancelAnimationFrame(frame);
		};
	}, []);
	
	
	// Reposition the bubble when the active view changes
	useLayoutEffect(() => {
		if (!isHydrated) {
			return;
		}
		
		updateBubble();
	}, [view, updateBubble, isHydrated]);
	
	
	// Reposition the bubble when the wrapper resizes
	//  -> Handles responsive device width changes
	useLayoutEffect(() => {
		if (!wrapperRef.current) {
			return;
		}
		
		const observer = new ResizeObserver(updateBubble);
		observer.observe(wrapperRef.current);
		
		return () => {
			observer.disconnect();
		};
	}, [updateBubble]);
	
	
	// Return JSX
	return (
		<Wrapper ref={wrapperRef}>
			{isHydrated && <ActiveBubble $isPositioned={isPositioned} style={bubbleStyle} />}
			<Button identifier='ALL' view={view} setView={setView} hasBubble={isPositioned}>
				All
			</Button>
			<Button identifier='PRODUCT' view={view} setView={setView} hasBubble={isPositioned}>
				Product
			</Button>
			<Button identifier='NEWS' view={view} setView={setView} hasBubble={isPositioned}>
				News
			</Button>
			<Button identifier='COMMUNITY' view={view} setView={setView} hasBubble={isPositioned}>
				Community
			</Button>
		</Wrapper>
	);
};

export default Toggle;
